import { useAuth } from '@clerk/clerk-react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Edit2,
    FileDown,
    Upload,
    Car,
    ChevronRight,
    UserX,
    UserCheck,
    Phone,
    AlertTriangle,
    CheckCircle2,
    Link as LinkIcon,
    Unlink,
    Plus,
    MessageSquare,
    Trash2,
    FileText,
    UserRound,
    Building2,
    Users,
    Download,
    Pencil,
    Check,
    X,
    Mail,
    Clock,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import {
    PageHeader,
    LoadingPage,
    ErrorPage,
    StatusPill,
} from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Select, Textarea } from '../../components/ui/Form.jsx';
import { DocumentUploadModal } from '../../components/DocumentUploadModal.jsx';
import { SectionDivider } from '../../components/ui/SectionDivider.jsx';
import { computeDisplayStatus } from '../../lib/computeStatus.js';
import { ServiceAreasCard } from './ServiceAreasCard.jsx';

// ...near other small components in the file:

/**
 * Status pill that shows the displayed status plus collapsed reasons.
 * On hover or tap, the reasons surface as a tooltip-style line below.
 * Compact when active, expanded when inactive with reasons to communicate.
 */
function StatusWithReasons({ entity }) {
    const { tone, label, reasons } = computeDisplayStatus(entity);

    if (reasons.length === 0) {
        return <StatusPill status={tone}>{label}</StatusPill>;
    }

    return (
        <div className="flex flex-col items-end gap-1">
            <StatusPill status={tone}>{label}</StatusPill>
            <div className="text-xs text-ink-subtle text-right">
                {reasons.join(' · ')}
            </div>
        </div>
    );
}

/**
 * Required document slots for a chauffeur (migration 004).
 * The three TFL + DVLA documents plus the profile picture.
 */
const REQUIRED_DOCS = [
    { type: 'driving_licence', label: 'Driving Licence', requiresExpiry: true },
    { type: 'phd_licence', label: 'PHD Licence', requiresExpiry: true },
    { type: 'phd_badge', label: 'PHD Badge', requiresExpiry: true },
    { type: 'profile_picture', label: 'Profile Picture', requiresExpiry: false },
];

/**
 * Required document slots for a fleet partner (round 7b.5).
 * Operator licence + profile picture. Driving/PHD licences are still tracked
 * if the partner also drives (shown as additional).
 */
const FLEET_PARTNER_DOCS = [
    { type: 'operator_licence', label: 'Operator Licence', requiresExpiry: true },
    { type: 'profile_picture', label: 'Profile Picture', requiresExpiry: false },
];

export function ChauffeurDetailPage() {
    const { id } = useParams();
    const api = useApi();
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const { isAdmin, isOwner } = useCurrentUser();

    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [showPairingModal, setShowPairingModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showEnableModal, setShowEnableModal] = useState(false);
    const [showAdditionalDocModal, setShowAdditionalDocModal] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [deletingDoc, setDeletingDoc] = useState(null);

    const driverQuery = useQuery({
        queryKey: ['driver', id],
        queryFn: () => api.get(`/drivers/${id}`),
    });

    const documentsQuery = useQuery({
        queryKey: ['documents', 'driver', id],
        queryFn: () => api.get(`/drivers/${id}/documents`),
        enabled: Boolean(id),
    });

    const pairingQuery = useQuery({
        queryKey: ['pairing', 'driver', id],
        queryFn: () => api.get(`/drivers/${id}/pairing`),
        enabled: Boolean(id),
    });

    const fleetQuery = useQuery({
        queryKey: ['driver-fleet', id],
        queryFn: () => api.get(`/drivers/${id}/fleet`),
        enabled: Boolean(id) && Boolean(driverQuery.data?.is_fleet_partner),
    });

    const notesQuery = useQuery({
        queryKey: ['notes', 'driver', id],
        queryFn: () => api.get(`/drivers/${id}/notes`),
        enabled: Boolean(id),
    });

    const queryClient = useQueryClient();
    const waiveMutation = useMutation({
        mutationFn: ({ doc_type, waived }) => api.post(`/drivers/${id}/document-waiver`, { doc_type, waived }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ['driver', id] });
            toast.success(vars.waived ? 'Marked not required' : 'Marked required');
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    if (driverQuery.isLoading) return <LoadingPage />;
    if (driverQuery.isError) return <ErrorPage message={driverQuery.error?.message} onRetry={driverQuery.refetch} />;

    const driver = driverQuery.data;
    const documents = documentsQuery.data?.documents || [];
    const pairing = pairingQuery.data;
    const notes = notesQuery.data?.notes || [];

    const isFleetPartner = Boolean(driver.is_fleet_partner);
    const isFleetChauffeur = Boolean(driver.fleet_partner_id);
    const isDirect = !isFleetPartner && !isFleetChauffeur;

    const fleetChauffeurs = fleetQuery.data?.chauffeurs || [];
    const fleetVehicles = fleetQuery.data?.vehicles || [];

    // Required doc slots depend on category
 const requiredDocSlots = isFleetPartner ? FLEET_PARTNER_DOCS : REQUIRED_DOCS;
    const requiredTypes = new Set(requiredDocSlots.map(d => d.type));
    const waivedTypes = new Set(driver.waived_doc_types || []);

    // Documents relevant to the CURRENT category go into required slots.
    // Additional documents = only 'other' (genuinely extra user-added docs).
    // Other docs (e.g. PHD Badge uploaded when direct, now viewing as partner) are
    // hidden — they stay on record in the database but don't display in this view.
    // If the chauffeur switches category back, they'll reappear in the required slots.
    // Collapse documents to one-per-type for the required slots. Filter out
// replaced/pending/rejected rows — only the latest approved current doc
// should populate a slot. When multiple approved rows exist for the same
// doc_type (shouldn't happen post-swap, but defensive), prefer the most
// recently uploaded one. Sort newest-first so Map.set() naturally retains
// the latest after iteration.
const sortedDocs = [...documents].sort(
    (a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)
);
const requiredDocsByType = new Map();
for (const d of sortedDocs) {
    if (!requiredTypes.has(d.doc_type)) continue;
    if (d.approval_status && d.approval_status !== 'approved') continue;
    if (d.replaced_by_document_id) continue;
    if (!requiredDocsByType.has(d.doc_type)) {
        requiredDocsByType.set(d.doc_type, d);
    }
}
    // Slots also show a pending upload ("Awaiting approval") when there's no
// approved doc for that type yet. Avatar keeps using approved docs only.
const slotDocsByType = new Map(requiredDocsByType);
for (const d of sortedDocs) {
    if (!requiredTypes.has(d.doc_type) || d.replaced_by_document_id) continue;
    if (d.approval_status === 'pending' && !slotDocsByType.has(d.doc_type)) {
        slotDocsByType.set(d.doc_type, d);
    }
}
    const additionalDocs = documents.filter(d =>
    d.doc_type === 'other'
    && (!d.approval_status || d.approval_status === 'approved')
    && !d.replaced_by_document_id
);

    const actions = isAdmin ? (
        <>
            {isDirect && (
                <Button variant="secondary" onClick={() => downloadCompliancePdf(getToken, id, driver)}>
                    <FileDown className="h-4 w-4" />
                    <span className="hidden sm:inline">Download profile PDF</span>
                </Button>
            )}
            <Button variant="secondary" onClick={() => navigate(`/chauffeurs/${id}/edit`)}>
                <Edit2 className="h-4 w-4" />
                <span className="hidden sm:inline">Edit</span>
            </Button>
        </>
    ) : null;

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <ChauffeurAvatar driverId={id} profilePictureDoc={requiredDocsByType.get('profile_picture')} />
                                                {driver.early_arrival_minutes ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 text-xs font-semibold uppercase tracking-wide">
                                Timekeeping
                            </span>
                        ) : null}
                        {driver.call_sign && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-ink text-paper font-mono text-sm font-medium tracking-wide">
                                {driver.call_sign}
                            </span>
                        )}
                    </span>
                }
                subtitle={driver.email}
                backTo="/chauffeurs"
                backLabel="Chauffeurs"
                actions={actions}
            />

            {driver.status !== 'active' && (
                <div className="card mb-5 border-amber-200 bg-amber-50">
                    <div className="card-body flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-status-warning flex-shrink-0" />
                        <div className="text-sm text-status-warning">
                            This chauffeur is <strong className="capitalize">{driver.status}</strong> and cannot be assigned to new jobs.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left: profile + category info + emergency contact + pairing */}
                <div className="lg:col-span-1 space-y-5">
    {isFleetPartner && <FleetPartnerCard driver={driver} />}
    {isFleetChauffeur && <WorksUnderCard driver={driver} />}
   <ProfileCard driver={driver} isDirect={isDirect} isFleetChauffeur={isFleetChauffeur} />
                        {!isFleetChauffeur && <ServiceAreasCard driver={driver} />}
    {/* 7d.5.5a — emergency contact moved into ProfileCard; component kept for now in case we revert */}
    {/* <EmergencyContactCard driver={driver} /> */}
    {isOwner && !isFleetChauffeur && <PaymentCard driver={driver} isOwner={isOwner} />}
    {isOwner && !isFleetChauffeur && <StatementsSummaryCard driverId={id} isOwner={isOwner} />}
   <PairingCard
    pairing={pairing}
    isAdmin={isAdmin}
    onOpen={() => setShowPairingModal(true)}
    onNavigate={navigate}
    />
                   {isOwner && driver.status === 'active' && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full text-status-danger hover:text-status-danger"
                                    onClick={() => setShowDisableModal(true)}
                                >
                                    <UserX className="h-4 w-4" />
                                    Disable chauffeur
                                </Button>
                            </div>
                        </div>
                    )}
                    {isOwner && driver.status !== 'active' && (
                        <div className="card">
                            <div className="card-body">
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setShowEnableModal(true)}
                                >
                                    <UserCheck className="h-4 w-4" />
                                    Re-enable chauffeur
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: documents + additional documents + notes */}
                <div className="lg:col-span-2 space-y-5">
                    {isFleetPartner && (
                        <>
                            <RateCardCard driver={driver} canEdit={isAdmin} />
                            <FleetChauffeursCard
                                chauffeurs={fleetChauffeurs}
                                isLoading={fleetQuery.isLoading}
                                onNavigate={navigate}
                                operatorId={id}
                                canAdd={isAdmin}
                            />
                            <FleetVehiclesCard
                                vehicles={fleetVehicles}
                                isLoading={fleetQuery.isLoading}
                                onNavigate={navigate}
                                operatorId={id}
                                canAdd={isAdmin}
                            />
                        </>
                    )}
                    <RequiredDocumentsCard
                        docSlots={requiredDocSlots}
                        docsByType={slotDocsByType}
                        isAdmin={isAdmin}
                        isFleetChauffeur={isFleetChauffeur}
                        waivedTypes={waivedTypes}
                        onWaive={isOwner ? (doc_type, waived) => waiveMutation.mutate({ doc_type, waived }) : null}
                        onUpload={(docConfig) => setUploadingDoc(docConfig)}
                        onPreview={(doc) => setPreviewDoc(doc)}
                    />
                    <AdditionalDocumentsCard
                        docs={additionalDocs}
                        isAdmin={isAdmin}
                        isOwner={isOwner}
                        onUpload={() => setShowAdditionalDocModal(true)}
                        onPreview={(doc) => setPreviewDoc(doc)}
                        onDelete={(doc) => setDeletingDoc(doc)}
                    />
                    <NotesCard
                        driver={driver}
                        driverId={id}
                        notes={notes}
                        isLoading={notesQuery.isLoading}
                        isOwner={isOwner}
                    />
                </div>
            </div>

            {/* Modals */}
            {uploadingDoc && (
                <DocumentUploadModal
                    ownerType="driver"
                    ownerId={id}
                    docType={uploadingDoc.type}
                    docLabel={uploadingDoc.label}
                    requiresExpiry={uploadingDoc.requiresExpiry}
                    open
                    onClose={() => setUploadingDoc(null)}
                />
            )}

            {showAdditionalDocModal && (
                <DocumentUploadModal
                    ownerType="driver"
                    ownerId={id}
                    docType="other"
                    docLabel="additional document"
                    requiresExpiry
                    expiryOptional
                    open
                    onClose={() => setShowAdditionalDocModal(false)}
                />
            )}

            {showPairingModal && (
                <PairingModal
                    driverId={id}
                    currentPairing={pairing}
                    open
                    onClose={() => setShowPairingModal(false)}
                />
            )}

            {showDisableModal && (
                <DisableModal
                    driverId={id}
                    driverName={`${driver.first_name} ${driver.last_name}`}
                    open
                    onClose={() => setShowDisableModal(false)}
                    onDisabled={() => {
                        setShowDisableModal(false);
                        driverQuery.refetch();
                    }}
                />
            )}

            {showEnableModal && (
                <EnableModal
                    driverId={id}
                    driverName={`${driver.first_name} ${driver.last_name}`}
                    open
                    onClose={() => setShowEnableModal(false)}
                    onEnabled={() => {
                        setShowEnableModal(false);
                        driverQuery.refetch();
                    }}
                />
            )}

          {previewDoc && (
    <DocumentPreviewModal
        document={previewDoc}
        open={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        ownerId={id}
        isOwner={isOwner}
    />
)}

            {deletingDoc && (
                <DeleteDocumentModal
                    driverId={id}
                    document={deletingDoc}
                    open
                    onClose={() => setDeletingDoc(null)}
                    onDeleted={() => setDeletingDoc(null)}
                />
            )}
        </div>
    );
}

// =============================================================================
// Fleet partner card — shown at top of left column when is_fleet_partner
// =============================================================================
function FleetPartnerCard({ driver }) {
    return (
        <div className="card border-blue-200">
            <div className="card-header bg-blue-50 border-b border-blue-200">
                <h2 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Operator
                </h2>
            </div>
            <div className="card-body">
                <dl className="space-y-3 text-sm">
                    <div>
                        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Company</dt>
                        <dd className="font-medium text-ink mt-0.5">{driver.fleet_partner_company_name || '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Operator licence number</dt>
                        <dd className="font-medium text-ink mt-0.5">{driver.fleet_partner_operator_licence_number || '—'}</dd>
                    </div>
                    {driver.fleet_partner_operator_licence_expires_at && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Licence expires</dt>
                            <dd className="font-medium text-ink mt-0.5">{formatDate(driver.fleet_partner_operator_licence_expires_at)}</dd>
                        </div>
                    )}
                    {driver.operator_address_line1 && (
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Operator address</dt>
                            <dd className="font-medium text-ink mt-0.5 break-words">{driver.operator_address_line1}</dd>
                        </div>
                    )}
                </dl>
                <p className="text-xs text-ink-subtle mt-4 pt-3 border-t border-paper-border-soft">
                    Operators manage their own documents. Excluded from Lanz CTS licensing authority reports.
                </p>
            </div>
        </div>
    );
}

// =============================================================================
// Works-under card — shown at top of left column when fleet_partner_id is set
// =============================================================================
function WorksUnderCard({ driver }) {
    return (
        <div className="card border-slate-200">
            <div className="card-header bg-slate-50 border-b border-slate-200">
                <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Works under
                </h2>
            </div>
            <div className="card-body">
                <div className="text-sm">
                    <div className="font-medium text-ink">
                        {driver.parent_partner_company_name || 'Operator'}
                    </div>
                    {(driver.parent_partner_first_name || driver.parent_partner_last_name) && (
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Primary contact: {driver.parent_partner_first_name} {driver.parent_partner_last_name}
                        </div>
                    )}
                </div>
                <p className="text-xs text-ink-subtle mt-4 pt-3 border-t border-paper-border-soft">
                    This chauffeur's documents are managed by their operator. Excluded from Lanz CTS licensing authority reports.
                </p>
            </div>
        </div>
    );
}

// =============================================================================
// Profile card
// =============================================================================
function ProfileCard({ driver, isDirect, isFleetChauffeur }) {
    // Icon-free, uppercase-label-on-top pattern matching VehicleDetailPage.
    // Reads as a clean spec sheet — no decorative icons next to obvious labels.
    // Home address (round 7b.6) sits inline as just another row.
    // Identity rows — personal/contact info, no licensing
const rows = [
    { label: 'Call sign', value: driver.call_sign || 'Not assigned' },
    { label: 'Phone', value: driver.phone },
    { label: 'Email', value: driver.email },
    { label: 'Home address', value: driver.home_address_line1 || '—' },
    { label: 'DOB', value: formatDate(driver.dob) },
    { label: 'NI Number', value: driver.ni_number || '—' },
];

// Licensing rows — grouped under their own SectionDivider, mirroring vehicle ProfileCard
const licensingRows = [
    { label: 'Authority', value: driver.licensing_authority },
    { label: 'PHD Licence', value: driver.phv_licence_number || '—' },
    { label: 'Driving Licence', value: driver.driving_licence_number || '—' },
];

// Engagement rows — start/end dates of working with Lanz CTS
const engagementRows = [
    { label: 'Started', value: formatDate(driver.start_date) },
    ...(driver.end_date ? [{ label: 'Ended', value: formatDate(driver.end_date) }] : []),
];

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-3">
    <h2 className="text-sm font-semibold text-ink">Profile</h2>
    <StatusWithReasons entity={driver} />
</div>
            <div className="card-body">
                {driver.pii_redacted && (
                    <div className="mb-3 text-xs text-ink-subtle italic">
                        Some personal details are hidden. Ask an owner for full access.
                    </div>
                )}
                <dl className="space-y-3">
    {/* Identity rows */}
    {rows.map(({ label, value }) => (
        <div key={label}>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</dt>
            <dd className="text-sm text-ink mt-0.5 break-words">{value || '—'}</dd>
        </div>
    ))}

    {/* Licensing — sub-grouped, matches vehicle ProfileCard pattern */}
    <SectionDivider label="Licensing" />
    {licensingRows.map(({ label, value }) => (
        <div key={label}>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</dt>
            <dd className="text-sm text-ink mt-0.5 break-words">{value || '—'}</dd>
        </div>
    ))}

    {/* Engagement — start/end dates */}
    <SectionDivider label="Engagement" />
    {engagementRows.map(({ label, value }) => (
        <div key={label}>
            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</dt>
            <dd className="text-sm text-ink mt-0.5 break-words">{value || '—'}</dd>
        </div>
    ))}

    {/* Emergency contact — sub-grouped under the Profile card, no separate card */}
    {(driver.emergency_contact_name
      || driver.emergency_contact_phone
      || driver.emergency_contact_relationship) && (
        <>
            <SectionDivider label="Emergency contact" />
            {driver.emergency_contact_name && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Name</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">{driver.emergency_contact_name}</dd>
                </div>
            )}
            {driver.emergency_contact_phone && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Phone</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">
                        <a href={`tel:${driver.emergency_contact_phone}`} className="text-ink hover:underline">
                            {driver.emergency_contact_phone}
                        </a>
                    </dd>
                </div>
            )}
            {driver.emergency_contact_relationship && (
                <div>
                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Relationship</dt>
                    <dd className="text-sm text-ink mt-0.5 break-words">{driver.emergency_contact_relationship}</dd>
                </div>
            )}
        </>
    )}
</dl>
                {isDirect && driver.include_in_tfl_report === false && (
                    <div className="mt-4 pt-4 border-t border-paper-border-soft">
                        <StatusPill status="neutral">Excluded from reports</StatusPill>
                    </div>
                )}
                {!isFleetChauffeur && (
                    <PortalAccessSubsection driver={driver} />
                )}
            </div>
        </div>
    );
}

// =============================================================================
// Emergency contact card
// =============================================================================
function EmergencyContactCard({ driver }) {
    const hasAny =
        driver.emergency_contact_name ||
        driver.emergency_contact_phone ||
        driver.emergency_contact_relationship;

    if (!hasAny) {
        return (
            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Emergency contact</h2>
                </div>
                <div className="card-body">
                    <p className="text-xs text-ink-subtle">
                        No emergency contact on file. Add one via the Edit form.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Emergency contact</h2>
            </div>
            <div className="card-body">
                <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-paper-hover flex items-center justify-center text-ink-muted flex-shrink-0">
                        <UserRound className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink">
                            {driver.emergency_contact_name || 'Unnamed contact'}
                        </div>
                        {driver.emergency_contact_relationship && (
                            <div className="text-xs text-ink-subtle mt-0.5 capitalize">
                                {driver.emergency_contact_relationship}
                            </div>
                        )}
                        {driver.emergency_contact_phone && (
                            <div className="text-xs text-ink-muted mt-1.5 flex items-center gap-1.5">
                                <Phone className="h-3 w-3" />
                                <a href={`tel:${driver.emergency_contact_phone}`} className="hover:text-ink">
                                    {driver.emergency_contact_phone}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// StatementsSummaryCard — Slice 7d.7 Piece 2 (chauffeur statement pool)
// =============================================================================
// Compact summary card for the chauffeur detail page. Shows pool size + total
// + oldest age. Links through to the full statements page.
//
// Hidden for fleet chauffeurs (their pay is settled with the partner, not
// directly by Lanz CTS).
//
// Drop in BELOW <PaymentCard /> in the left column.
//
// Owner-gated. The pool endpoint itself requires owner auth — surfacing the
// card to non-owners would just show a 403 on click.
// =============================================================================

function StatementsSummaryCard({ driverId, isOwner }) {
    const api = useApi();
    const navigate = useNavigate();

    const poolQuery = useQuery({
        queryKey: ['statement-pool', driverId],
        queryFn: () => api.get(`/drivers/${driverId}/statement-pool`),
        enabled: isOwner && Boolean(driverId),
    });

    if (!isOwner) return null;

    const lines = poolQuery.data?.lines || [];
    const lineCount = lines.length;

    // Total — sum of amount_ex_vat across all lines (gross VAT calc happens
    // on the statement itself; the pool summary is just a rough indicator).
    const totalEx = lines.reduce((sum, l) => sum + (Number(l.amount_ex_vat) || 0), 0);

    // Oldest line — by effective_date (pickup_date for booking-tied,
    // shift_date for free-standing). Banner appears in the body if > 14d.
    const oldestDate = lines.length > 0
        ? lines.reduce((oldest, l) => {
            const d = l.effective_date;
            if (!d) return oldest;
            return !oldest || d < oldest ? d : oldest;
        }, null)
        : null;

    const oldestDays = oldestDate ? daysSince(oldestDate) : null;
    const isStale = oldestDays != null && oldestDays > 14;

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink">Statements</h2>
                {!poolQuery.isLoading && (
                    <button
                        type="button"
                        onClick={() => navigate(`/chauffeurs/${driverId}/statements`)}
                        className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1"
                    >
                        View
                        <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <div className="card-body">
                {poolQuery.isLoading ? (
                    <p className="text-sm text-ink-subtle">Loading…</p>
                ) : poolQuery.isError ? (
                    <p className="text-sm text-status-danger">Could not load pool</p>
                ) : lineCount === 0 ? (
                    <div>
                        <p className="text-sm text-ink-subtle">No lines waiting.</p>
                        <p className="text-xs text-ink-faint mt-1">
                            Lock the chauffeur side of bookings to add lines here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <dl className="space-y-2">
                            <div className="flex items-baseline justify-between">
                                <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Lines waiting</dt>
                                <dd className="text-sm text-ink font-medium">{lineCount}</dd>
                            </div>
                            <div className="flex items-baseline justify-between">
                                <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Total (ex. VAT)</dt>
                                <dd className="text-sm text-ink font-medium">£{totalEx.toFixed(2)}</dd>
                            </div>
                            {oldestDays != null && (
                                <div className="flex items-baseline justify-between">
                                    <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Oldest line</dt>
                                    <dd className={`text-sm font-medium ${isStale ? 'text-status-warning' : 'text-ink'}`}>
                                        {oldestDays === 0 ? 'Today' : `${oldestDays}d ago`}
                                    </dd>
                                </div>
                            )}
                        </dl>

                        {isStale && (
                            <div className="text-xs text-status-warning bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                                Some lines are over 14 days old.
                            </div>
                        )}

                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => navigate(`/chauffeurs/${driverId}/statements`)}
                            className="w-full"
                        >
                            <FileText className="h-3.5 w-3.5" />
                            Open statements
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function daysSince(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.floor((today - d) / (1000 * 60 * 60 * 24));
}

// =============================================================================
// PaymentCard — Slice 7d.7 Piece 1 (driver payment settings)
// =============================================================================
// Owner-only card showing how this chauffeur is paid. Drives the statement
// generator (Piece 2/3 of the slice) — pay_type determines which PDF
// template renders, vat fields determine whether VAT is added, hourly_rate
// is the reference rate for per-hour drivers entering shift pay.
//
// Lifecycle:
//   - Read mode: shows current settings as a dl, with Edit pencil for owner.
//   - Edit mode: form fields with conditional reveals (rate only if per_hour,
//     VAT number only if registered). Save → PATCH /drivers/:id → toast +
//     query invalidate → back to read mode.
//
// Cross-field validation lives in the backend (already shipped in Piece 1
// backend); this card does light client-side guarding to prevent obvious
// submit errors but trusts the server as source of truth.
//
// Drop in BELOW <ProfileCard /> in the left column — payment is metadata
// about the chauffeur similar to profile, separate from operational stuff
// (pairing) and lifecycle (disable/enable).
// =============================================================================

function PaymentCard({ driver, isOwner }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);

    // Local draft state. Initialised from driver on entry to edit mode so
    // we don't fight the parent's refetched value mid-edit.
    const [payType, setPayType]               = useState(driver.pay_type || 'per_job');
    const [vatRegistered, setVatRegistered]   = useState(Boolean(driver.vat_registered));
    const [vatNumber, setVatNumber]           = useState(driver.vat_number || '');
    const [hourlyRatePounds, setHourlyRatePounds] = useState(
        driver.hourly_rate_pence != null
            ? (driver.hourly_rate_pence / 100).toFixed(2)
            : ''
    );
    const [commissionPct, setCommissionPct] = useState(
        driver.commission_pct != null ? String(Number(driver.commission_pct)) : ''
    );

    // Re-seed when entering edit mode so we always start from current truth.
    function startEdit() {
        setPayType(driver.pay_type || 'per_job');
        setVatRegistered(Boolean(driver.vat_registered));
        setVatNumber(driver.vat_number || '');
        setHourlyRatePounds(
            driver.hourly_rate_pence != null
                ? (driver.hourly_rate_pence / 100).toFixed(2)
                : ''
        );
        setCommissionPct(driver.commission_pct != null ? String(Number(driver.commission_pct)) : '');
        setEditing(true);
    }

    function cancel() {
        setEditing(false);
    }

    const saveMutation = useMutation({
        mutationFn: (payload) => api.patch(`/drivers/${driver.id}`, payload),
        onSuccess: () => {
            toast.success('Payment settings saved');
            queryClient.invalidateQueries({ queryKey: ['driver', driver.id] });
            setEditing(false);
        },
        onError: (err) => {
            // Backend returns fieldErrors on validation failure — surface the
            // first one if present, otherwise the generic message.
            const fieldErrors = err?.response?.data?.fieldErrors;
            if (fieldErrors) {
                const firstField = Object.keys(fieldErrors)[0];
                const firstMsg = fieldErrors[firstField]?.[0];
                if (firstMsg) {
                    toast.error(firstMsg);
                    return;
                }
            }
            const msg = err?.response?.data?.error || err?.message || 'Could not save';
            toast.error(msg);
        },
    });

    function handleSave() {
        // Build payload, sending only changed fields. Mirrors the backend
        // PATCH semantics — partial updates are normal.
        const payload = {};

        if (payType !== (driver.pay_type || 'per_job')) {
            payload.pay_type = payType;
        }
        if (vatRegistered !== Boolean(driver.vat_registered)) {
            payload.vat_registered = vatRegistered;
        }

        // VAT number — send when checked AND value differs (or going from
        // unchecked to checked). Trim to avoid whitespace-only values.
        const vatNumberClean = vatNumber.trim().toUpperCase();
        const currentVatNumber = driver.vat_number || '';
        if (vatRegistered && vatNumberClean !== currentVatNumber) {
            payload.vat_number = vatNumberClean || null;
        }
        // If unchecking VAT, explicitly null the number so backend clears it.
        if (!vatRegistered && currentVatNumber) {
            payload.vat_number = null;
        }

        // Hourly rate — convert £ string to pence. Send only when changed.
        const ratePence = hourlyRatePounds.trim() === ''
            ? null
            : Math.round(parseFloat(hourlyRatePounds) * 100);
        const currentRatePence = driver.hourly_rate_pence != null
            ? Number(driver.hourly_rate_pence)
            : null;
        if (payType === 'per_hour' && ratePence !== currentRatePence) {
            payload.hourly_rate_pence = ratePence;
        }
        // If switching to per_job, explicitly null the rate.
        if (payType === 'per_job' && currentRatePence != null) {
            payload.hourly_rate_pence = null;
        }

        // Commission % — company's cut of fares. Send only when changed.
        const commissionClean = commissionPct.trim() === '' ? null : parseFloat(commissionPct);
        const currentCommission = driver.commission_pct != null ? Number(driver.commission_pct) : null;
        if (commissionClean !== currentCommission) {
            payload.commission_pct = commissionClean;
        }

        // Light client-side guard — backend will reject these too, but a
        // toast feels faster than a round trip.
        if (vatRegistered && !vatNumberClean && !currentVatNumber) {
            toast.error('VAT number is required when VAT registered');
            return;
        }
        if (payType === 'per_hour' && (!ratePence || ratePence <= 0)) {
            toast.error('Hourly rate is required for per-hour chauffeurs');
            return;
        }

        // No changes? Bail without firing.
        if (Object.keys(payload).length === 0) {
            toast('No changes to save');
            setEditing(false);
            return;
        }

        saveMutation.mutate(payload);
    }

    const saving = saveMutation.isPending;

    // ---- Read mode ----------------------------------------------------------
    if (!editing) {
        return (
            <div className="card">
                <div className="card-header flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-ink">Payment</h2>
                    {isOwner && (
                        <button
                            type="button"
                            onClick={startEdit}
                            className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1"
                        >
                            <Edit2 className="h-3.5 w-3.5" />
                            Edit
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <dl className="space-y-3">
                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Pay type</dt>
                            <dd className="text-sm text-ink mt-0.5">
                                {driver.pay_type === 'per_hour' ? 'Per hour' : 'Per job'}
                            </dd>
                        </div>

                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Commission (company keeps)</dt>
                            <dd className="text-sm text-ink mt-0.5">
                                {driver.commission_pct != null
                                    ? `${Number(driver.commission_pct)}%`
                                    : <span className="text-ink-subtle italic">Not set</span>}
                            </dd>
                        </div>

                        {driver.pay_type === 'per_hour' && (
                            <div>
                                <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Hourly rate</dt>
                                <dd className="text-sm text-ink mt-0.5">
                                    {driver.hourly_rate_pence != null
                                        ? `£${(driver.hourly_rate_pence / 100).toFixed(2)} / hr`
                                        : <span className="text-ink-subtle italic">Not set</span>}
                                </dd>
                            </div>
                        )}

                        <div>
                            <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">VAT registered</dt>
                            <dd className="text-sm text-ink mt-0.5">
                                {driver.vat_registered ? 'Yes' : 'No'}
                            </dd>
                        </div>

                        {driver.vat_registered && (
                            <div>
                                <dt className="text-xs text-ink-subtle uppercase tracking-wider font-medium">VAT number</dt>
                                <dd className="text-sm text-ink mt-0.5 font-mono">
                                    {driver.vat_number || <span className="text-ink-subtle italic">Not set</span>}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>
        );
    }

    // ---- Edit mode ----------------------------------------------------------
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Payment</h2>
            </div>
            <div className="card-body space-y-5">
                {/* Pay type — radio */}
                <div>
                    <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium mb-2">
                        Pay type
                    </div>
                    <div className="flex gap-1 p-1 bg-canvas rounded-lg w-fit">
                        {[
                            { value: 'per_job', label: 'Per job' },
                            { value: 'per_hour', label: 'Per hour' },
                        ].map((m) => (
                            <button
                                key={m.value}
                                type="button"
                                onClick={() => setPayType(m.value)}
                                disabled={saving}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    payType === m.value
                                        ? 'bg-paper text-ink shadow-sm'
                                        : 'text-ink-subtle hover:text-ink'
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Hourly rate — only when per_hour */}
                {payType === 'per_hour' && (
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Hourly rate (£)
                        </label>
                        <div className="relative max-w-[160px]">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle pointer-events-none">£</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={hourlyRatePounds}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                                        setHourlyRatePounds(v);
                                    }
                                }}
                                disabled={saving}
                                placeholder="20.00"
                                className="w-full pl-6 pr-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40"
                            />
                        </div>
                     <p className="text-xs text-ink-subtle mt-1">
                            Reference rate. Statement totals come from actual line entries.
                        </p>
                    </div>
                )}

                {/* Commission — company's cut of fares. Used by the chauffeur-side
                    "Prefill from commission" button on the billing ledger. */}
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Commission (%)
                    </label>
                    <div className="relative max-w-[160px]">
                        <input
                            type="text"
                            inputMode="decimal"
                            value={commissionPct}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d{0,3}(\.\d{0,2})?$/.test(v)) {
                                    setCommissionPct(v);
                                }
                            }}
                            disabled={saving}
                            placeholder="20"
                            className="w-full pl-2 pr-6 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-ink-subtle pointer-events-none">%</span>
                    </div>
                    <p className="text-xs text-ink-subtle mt-1">
                        The percentage Lanz CTS keeps. Chauffeur receives the remainder of fares (ex-VAT).
                    </p>
                </div>

                <div className="border-t border-paper-border-soft pt-4">
                    {/* VAT registered — checkbox */}
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={vatRegistered}
                            onChange={(e) => setVatRegistered(e.target.checked)}
                            disabled={saving}
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-sm font-medium text-ink">VAT registered</div>
                            <div className="text-xs text-ink-subtle mt-0.5">
                                Tick if this chauffeur charges VAT to Lanz CTS for their services.
                            </div>
                        </div>
                    </label>

                    {/* VAT number — only when checked */}
                    {vatRegistered && (
                        <div className="mt-3 ml-6">
                            <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                                VAT number
                            </label>
                            <input
                                type="text"
                                value={vatNumber}
                                onChange={(e) => setVatNumber(e.target.value)}
                                disabled={saving}
                                placeholder="GB123456789"
                                className="w-full max-w-[220px] px-2 py-1.5 text-sm font-mono border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40"
                            />
                            <p className="text-xs text-ink-subtle mt-1">
                                GB followed by 9 or 12 digits.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Action footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-2 pt-2 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={cancel} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} loading={saving}>
                    Save
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// Pairing card
// =============================================================================
function PairingCard({ pairing, isAdmin, onOpen, onNavigate }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Paired vehicle</h2>
            </div>
            <div className="card-body">
                {pairing ? (
                    <div>
                        <button
                            type="button"
                            onClick={() => onNavigate(`/vehicles/${pairing.vehicle_id}`)}
                            className="w-full text-left flex items-start gap-3 -mx-2 -my-1 px-2 py-1 rounded hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="h-9 w-9 rounded-md bg-paper-hover flex items-center justify-center text-ink-muted flex-shrink-0">
                                <Car className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">{pairing.vehicle_nickname || pairing.registration}</div>
                                <div className="text-xs text-ink-subtle">{pairing.make} {pairing.model}</div>
                                <div className="text-xs text-ink-subtle mt-0.5">{pairing.registration}</div>
                            </div>
                        </button>
                        {pairing.notes && (
                            <p className="mt-3 text-xs text-ink-muted italic">{pairing.notes}</p>
                        )}
                        <p className="mt-3 text-xs text-ink-subtle">
                            Paired {formatDate(pairing.started_at)}
                        </p>
                        {isAdmin && (
                            <div className="mt-4 flex gap-2">
                                <Button size="sm" variant="secondary" onClick={onOpen} className="flex-1">
                                    <LinkIcon className="h-3.5 w-3.5" />
                                    Change
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <p className="text-sm text-ink-subtle">No usual vehicle paired.</p>
                        <p className="text-xs text-ink-faint mt-1">Pairing is optional — useful for subcontractors with their own car.</p>
                        {isAdmin && (
                            <Button size="sm" variant="secondary" onClick={onOpen} className="mt-4 w-full">
                                <LinkIcon className="h-3.5 w-3.5" />
                                Pair vehicle
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// Fleet roster cards — shown on a fleet partner's detail page (read-only)
// =============================================================================
// Lists chauffeurs working under this partner and vehicles registered to them.
// Each row links to its own detail page. Status pill is the manual
// active/inactive flag only (fleet entities are compliance-opted-out).
// =============================================================================
// =============================================================================
// Rate card — operators only (Oct 2026)
// =============================================================================
// Reference card for pricing jobs with an operator: hourly rate + minimum
// hours per vehicle, plus cancellation policy and notes. Stored as JSON on
// drivers.rate_card. Everything optional — enter whatever the operator gives.
// Display-only: it does not price bookings automatically.
// =============================================================================
// Symbols for the common ones; anything else shows as typed, e.g. "SEK 650.00".
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
const CURRENCY_SUGGESTIONS = ['EUR', 'GBP', 'USD', 'SEK', 'NOK', 'DKK', 'CHF', 'PLN', 'CZK', 'HUF', 'AED'];
const RATE_VEHICLE_SUGGESTIONS = ['Business', 'First', 'SUV', 'XL', 'Minibus'];

function money(amount, currency) {
    if (amount == null || amount === '' || !Number.isFinite(Number(amount))) return '—';
    const code = String(currency || '').trim();
    const sym = CURRENCY_SYMBOLS[code.toUpperCase()] ?? (code ? `${code} ` : '');
    return `${sym}${Number(amount).toFixed(2)}`;
}

// Returns { ex, inc } for a line, or nulls when VAT unknown.
function rateExInc(line) {
    const r = Number(line.hourly_rate);
    if (!Number.isFinite(r) || line.hourly_rate == null || line.hourly_rate === '') return { ex: null, inc: null };
    const v = Number(line.vat_rate);
    if (line.vat_rate == null || line.vat_rate === '' || !Number.isFinite(v)) {
        return line.vat_included ? { ex: null, inc: r } : { ex: r, inc: null };
    }
    return line.vat_included
        ? { ex: r / (1 + v / 100), inc: r }
        : { ex: r, inc: r * (1 + v / 100) };
}

function RateCardCard({ driver, canEdit }) {
    const [editing, setEditing] = useState(false);
    const rc = driver.rate_card || null;
    // Hourly rates first (the main thing), one-way transfers after.
    const lines = [...(rc?.lines || [])].sort(
        (a, b) => (a.rate_type === 'oneway' ? 1 : 0) - (b.rate_type === 'oneway' ? 1 : 0)
    );
    const hasAnything = lines.length > 0 || rc?.cancellation_policy || rc?.notes;

    return (
        <div className="card">
            <div className="card-header flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Rate card
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        {rc?.updated_at
                            ? `Last updated ${formatDate(rc.updated_at)}`
                            : 'Hourly rates, minimum hours and cancellation policy from this operator.'}
                    </p>
                </div>
                {canEdit && (
                    <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                        <Pencil className="h-3.5 w-3.5" />
                        {hasAnything ? 'Edit' : 'Add rates'}
                    </Button>
                )}
            </div>

            {!hasAnything ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No rates entered yet.</p>
                </div>
            ) : (
                <>
                    {lines.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-paper-hover border-b border-paper-border text-xs font-medium text-ink-subtle uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-6 py-2.5">Vehicle</th>
                                        <th className="text-left px-4 py-2.5">Type</th>
                                        <th className="text-right px-4 py-2.5">Rate ex VAT</th>
                                        <th className="text-right px-4 py-2.5">Rate inc VAT</th>
                                        <th className="text-right px-6 py-2.5">Min. hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l, i) => {
                                        const { ex, inc } = rateExInc(l);
                                        const isOneway = l.rate_type === 'oneway';
                                        const per = isOneway ? '' : ' /hr';
                                        return (
                                            <tr key={i} className="border-b border-paper-border-soft last:border-0">
                                                <td className="px-6 py-3">
                                                    <div className="font-medium text-ink">{l.label || '—'}</div>
                                                    {l.notes && <div className="text-xs text-ink-subtle mt-0.5">{l.notes}</div>}
                                                </td>
                                                <td className="px-4 py-3 text-ink-muted whitespace-nowrap">{isOneway ? 'One way' : 'Hourly'}</td>
                                                <td className="px-4 py-3 text-right text-ink whitespace-nowrap">{ex != null ? money(ex, l.currency) + per : '—'}</td>
                                                <td className="px-4 py-3 text-right text-ink whitespace-nowrap">
                                                    {inc != null ? money(inc, l.currency) + per : '—'}
                                                    {l.vat_rate != null && l.vat_rate !== '' && (
                                                        <div className="text-[11px] text-ink-subtle">VAT {Number(l.vat_rate)}%</div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-3 text-right text-ink">
                                                    {!isOneway && l.min_hours != null && l.min_hours !== '' ? `${Number(l.min_hours)} hrs` : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {(rc?.cancellation_policy || rc?.notes) && (
                        <div className="card-body space-y-3 border-t border-paper-border-soft">
                            {rc?.cancellation_policy && (
                                <div>
                                    <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Cancellation policy</div>
                                    <p className="text-sm text-ink mt-0.5 whitespace-pre-wrap">{rc.cancellation_policy}</p>
                                </div>
                            )}
                            {rc?.notes && (
                                <div>
                                    <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium">Notes</div>
                                    <p className="text-sm text-ink mt-0.5 whitespace-pre-wrap">{rc.notes}</p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {editing && (
                <RateCardModal driver={driver} open onClose={() => setEditing(false)} />
            )}
        </div>
    );
}

const EMPTY_RATE_LINE = { label: '', rate_type: 'hourly', hourly_rate: '', currency: 'EUR', vat_rate: '', vat_included: false, min_hours: '', notes: '' };

function RateCardModal({ driver, open, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const rc = driver.rate_card || {};
    const [lines, setLines] = useState(
        (rc.lines && rc.lines.length > 0)
            ? rc.lines.map((l) => ({
                ...EMPTY_RATE_LINE,
                ...l,
                hourly_rate: l.hourly_rate ?? '',
                vat_rate: l.vat_rate ?? '',
                min_hours: l.min_hours ?? '',
                notes: l.notes || '',
            }))
            : [{ ...EMPTY_RATE_LINE }]
    );
    const [policy, setPolicy] = useState(rc.cancellation_policy || '');
    const [notes, setNotes] = useState(rc.notes || '');

    const num = (v) => (v === '' || v == null ? null : Number(v));
    const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

    const mutation = useMutation({
        mutationFn: () => api.patch(`/drivers/${driver.id}`, {
            rate_card: {
                lines: lines
                    .filter((l) => l.label.trim() || l.hourly_rate !== '' || l.min_hours !== '')
                    .map((l) => ({
                        label: l.label.trim(),
                        rate_type: l.rate_type === 'oneway' ? 'oneway' : 'hourly',
                        hourly_rate: num(l.hourly_rate),
                        currency: (l.currency || '').trim().toUpperCase() || 'EUR',
                        vat_rate: num(l.vat_rate),
                        vat_included: !!l.vat_included,
                        min_hours: l.rate_type === 'oneway' ? null : num(l.min_hours),
                        notes: (l.notes || '').trim(),
                    })),
                cancellation_policy: policy.trim(),
                notes: notes.trim(),
            },
        }),
        onSuccess: () => {
            toast.success('Rate card saved');
            queryClient.invalidateQueries({ queryKey: ['driver', driver.id] });
            onClose();
        },
        onError: (err) => toast.error(err?.message || 'Could not save rate card'),
    });

    const cell = 'w-full px-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30';

    return (
        <Modal open={open} onClose={onClose} title="Rate card" description="Fill in whatever the operator gave you — every field is optional." size="lg">
            <div className="space-y-4">
                <datalist id="rate-currency-suggestions">
                    {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                </datalist>
                <datalist id="rate-vehicle-suggestions">
                    {RATE_VEHICLE_SUGGESTIONS.map((v) => <option key={v} value={v} />)}
                </datalist>

                <div className="space-y-3">
                    {lines.map((l, i) => {
                        const { ex, inc } = rateExInc({ ...l, hourly_rate: num(l.hourly_rate), vat_rate: num(l.vat_rate) });
                        return (
                            <div key={i} className="rounded-md border border-paper-border p-3 space-y-2">
                                <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
                                    <div>
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">Type</label>
                                        <select value={l.rate_type || 'hourly'} onChange={(e) => setLine(i, { rate_type: e.target.value })} className={cell}>
                                            <option value="hourly">Hourly</option>
                                            <option value="oneway">One way</option>
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">Vehicle</label>
                                        <input list="rate-vehicle-suggestions" value={l.label} maxLength={80}
                                            onChange={(e) => setLine(i, { label: e.target.value })}
                                            placeholder="e.g. Business (E-Class)" className={cell} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">{l.rate_type === 'oneway' ? 'Price' : 'Per hour'}</label>
                                        <input type="number" min="0" step="0.01" value={l.hourly_rate}
                                            onChange={(e) => setLine(i, { hourly_rate: e.target.value })} className={cell} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">Currency</label>
                                        <input list="rate-currency-suggestions" value={l.currency} maxLength={12}
                                            onChange={(e) => setLine(i, { currency: e.target.value })}
                                            placeholder="EUR" className={cell} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">VAT %</label>
                                        <input type="number" min="0" max="100" step="0.1" value={l.vat_rate}
                                            onChange={(e) => setLine(i, { vat_rate: e.target.value })}
                                            placeholder="e.g. 19" className={cell} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] text-ink-subtle uppercase tracking-wide mb-1">Min. hours</label>
                                        <input type="number" min="0" step="0.5" value={l.rate_type === 'oneway' ? '' : l.min_hours}
                                            disabled={l.rate_type === 'oneway'}
                                            onChange={(e) => setLine(i, { min_hours: e.target.value })}
                                            className={`${cell} disabled:opacity-40`} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <select value={l.vat_included ? 'inc' : 'ex'}
                                        onChange={(e) => setLine(i, { vat_included: e.target.value === 'inc' })}
                                        className="text-xs px-2 py-1 border border-paper-border rounded bg-paper text-ink">
                                        <option value="ex">Rate is ex VAT</option>
                                        <option value="inc">Rate is inc VAT</option>
                                    </select>
                                    <input value={l.notes} maxLength={300}
                                        onChange={(e) => setLine(i, { notes: e.target.value })}
                                        placeholder={l.rate_type === 'oneway' ? 'Route, e.g. BER airport → city centre' : 'Note for this vehicle (optional)'}
                                        className="flex-1 min-w-[160px] text-xs px-2 py-1 border border-paper-border rounded bg-paper text-ink" />
                                    <span className="text-xs text-ink-subtle">
                                        {ex != null && `${money(ex, l.currency)} ex`}
                                        {ex != null && inc != null && ' · '}
                                        {inc != null && `${money(inc, l.currency)} inc`}
                                    </span>
                                    <button type="button" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
                                        className="ml-auto p-1 text-ink-subtle hover:text-status-danger" aria-label="Remove vehicle">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    <Button type="button" size="sm" variant="secondary"
                        onClick={() => setLines((ls) => [...ls, { ...EMPTY_RATE_LINE, currency: ls[ls.length - 1]?.currency || 'EUR', vat_rate: ls[ls.length - 1]?.vat_rate ?? '' }])}>
                        <Plus className="h-3.5 w-3.5" />
                        Add vehicle
                    </Button>
                </div>

                <Textarea label="Cancellation policy" value={policy} onChange={(e) => setPolicy(e.target.value)}
                    rows={3} maxLength={2000} placeholder="e.g. Free up to 24h before pickup, 50% within 24h, 100% no-show" />
                <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2} maxLength={2000} placeholder="Anything else they mentioned" />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>Save rate card</Button>
            </div>
        </Modal>
    );
}

function FleetRosterStatusPill({ status }) {
    return status === 'active'
        ? <StatusPill status="success">Active</StatusPill>
        : <StatusPill status="neutral">Inactive</StatusPill>;
}

function FleetChauffeursCard({ chauffeurs, isLoading, onNavigate, operatorId, canAdd }) {
    return (
        <div className="card">
            <div className="card-header flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Chauffeurs
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Chauffeurs working under this operator.
                    </p>
                </div>
                {canAdd && operatorId && (
                    <Button size="sm" variant="secondary" onClick={() => onNavigate(`/chauffeurs/new?operator=${operatorId}`)}>
                        <Plus className="h-3.5 w-3.5" />
                        Add chauffeur
                    </Button>
                )}
            </div>
            {isLoading ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">Loading…</p>
                </div>
            ) : chauffeurs.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">None linked yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {chauffeurs.map((c) => (
                        <li
                            key={c.id}
                            onClick={() => onNavigate(`/chauffeurs/${c.id}`)}
                            className="px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-ink truncate">
                                        {c.first_name} {c.last_name}
                                    </span>
                                    {c.call_sign && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-ink text-paper font-mono text-2xs font-medium">
                                            {c.call_sign}
                                        </span>
                                    )}
                                </div>
                                {c.phone && (
                                    <div className="text-xs text-ink-subtle mt-0.5 truncate">{c.phone}</div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <FleetRosterStatusPill status={c.status} />
                                <ChevronRight className="h-4 w-4 text-ink-faint" />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FleetVehiclesCard({ vehicles, isLoading, onNavigate, operatorId, canAdd }) {
    const CLASS_LABELS = { business: 'Business', first: 'First', suv: 'SUV', xl: 'XL' };
    return (
        <div className="card">
            <div className="card-header flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Car className="h-4 w-4" />
                        Vehicles
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Vehicles registered to this operator.
                    </p>
                </div>
                {canAdd && operatorId && (
                    <Button size="sm" variant="secondary" onClick={() => onNavigate(`/vehicles/new?operator=${operatorId}`)}>
                        <Plus className="h-3.5 w-3.5" />
                        Add vehicle
                    </Button>
                )}
            </div>
            {isLoading ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">Loading…</p>
                </div>
            ) : vehicles.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">None linked yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {vehicles.map((v) => (
                        <li
                            key={v.id}
                            onClick={() => onNavigate(`/vehicles/${v.id}`)}
                            className="px-6 py-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-ink text-paper font-mono text-2xs font-medium">
                                        {v.registration}
                                    </span>
                                    <span className="text-sm font-medium text-ink truncate">
                                        {v.make} {v.model}
                                    </span>
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    {CLASS_LABELS[v.vehicle_class] || v.vehicle_class}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <FleetRosterStatusPill status={v.status} />
                                <ChevronRight className="h-4 w-4 text-ink-faint" />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// =============================================================================
// Required documents card
// =============================================================================
function RequiredDocumentsCard({ docSlots, docsByType, isAdmin, isFleetChauffeur, waivedTypes = new Set(), onWaive, onUpload, onPreview }) {
    if (isFleetChauffeur) {
        // Fleet chauffeurs have no compliance slots (the partner manages those)
        // and no portal access — but the profile picture is a Lanz-CTS-side
        // presentation asset (customer emails + customer portal), so the office
        // uploads it directly here. Same documents/R2 path as any other admin
        // upload; the header avatar reads docsByType('profile_picture').
        const profileConfig = { type: 'profile_picture', label: 'Profile Picture', requiresExpiry: false };
        const profileDoc = docsByType.get('profile_picture');
        return (
            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Documents</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Compliance documents are managed by the operator. The profile picture is office-managed — it shows on customer emails and the customer portal. Other files can go under Additional documents below.
                    </p>
                </div>
                <div className="divide-y divide-paper-border-soft">
                   <DocumentRow
                        label={profileConfig.label}
                        requiresExpiry={false}
                        document={profileDoc}
                        isAdmin={isAdmin}
                        waived={waivedTypes.has('profile_picture')}
                        onWaive={onWaive ? (w) => onWaive('profile_picture', w) : null}
                        onUpload={() => onUpload(profileConfig)}
                        onPreview={profileDoc ? () => onPreview(profileDoc) : null}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Required documents</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    {docSlots.length === FLEET_PARTNER_DOCS.length
                        ? 'The operator licence plus a profile picture.'
                        : 'The four documents every chauffeur must have on file.'}
                </p>
            </div>
            <div className="divide-y divide-paper-border-soft">
                {docSlots.map((docConfig) => {
                    const doc = docsByType.get(docConfig.type);
                    return (
                      <DocumentRow
                            key={docConfig.type}
                            label={docConfig.label}
                            requiresExpiry={docConfig.requiresExpiry}
                            document={doc}
                            isAdmin={isAdmin}
                            waived={waivedTypes.has(docConfig.type)}
                            onWaive={onWaive ? (w) => onWaive(docConfig.type, w) : null}
                            onUpload={() => onUpload(docConfig)}
                            onPreview={doc ? () => onPreview(doc) : null}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// =============================================================================
// Additional documents card
// =============================================================================
function AdditionalDocumentsCard({ docs, isAdmin, isOwner, onUpload, onPreview, onDelete }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink">Additional documents</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Optional extra documents — passport, right to work, DBS, etc.
                    </p>
                </div>
                {isAdmin && (
                    <Button size="sm" variant="secondary" onClick={onUpload}>
                        <Plus className="h-3.5 w-3.5" />
                        Add
                    </Button>
                )}
            </div>
            {docs.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No additional documents.</p>
                </div>
            ) : (
                <div className="divide-y divide-paper-border-soft">
                    {docs.map((doc) => (
                        <DocumentRow
                            key={doc.id}
                            label={doc.custom_label || formatDocType(doc.doc_type)}
                            requiresExpiry={Boolean(doc.expires_at)}
                            document={doc}
                            isAdmin={isAdmin}
                            isAdditional
                            onPreview={() => onPreview(doc)}
                            onDelete={isOwner && onDelete ? () => onDelete(doc) : null}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// Notes card
// =============================================================================
function NotesCard({ driver, driverId, notes, isLoading, isOwner }) {
    const [composerText, setComposerText] = useState('');
    const api = useApi();
    const queryClient = useQueryClient();

    const addNote = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/notes`, { note_text: composerText.trim() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes', 'driver', driverId] });
            setComposerText('');
            toast.success('Note added');
        },
        onError: (err) => toast.error(err.message || 'Could not add note'),
    });

    const deleteNote = useMutation({
        mutationFn: (noteId) => api.delete(`/drivers/notes/${noteId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes', 'driver', driverId] });
            toast.success('Note deleted');
        },
        onError: (err) => toast.error(err.message || 'Could not delete note'),
    });

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Notes</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Timestamped notes — licence points, preferences, follow-ups, anything worth remembering.
                </p>
            </div>

            {driver && <EarlyArrivalRow driver={driver} canEdit={isOwner} />}

            <div className="card-body border-b border-paper-border-soft">
                <Textarea
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    placeholder="Add a note…"
                    rows={2}
                    maxLength={5000}
                />
                <div className="mt-3 flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => addNote.mutate()}
                        disabled={!composerText.trim() || addNote.isPending}
                        loading={addNote.isPending}
                    >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Add note
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">Loading…</p>
                </div>
            ) : notes.length === 0 ? (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-2">No notes yet.</p>
                </div>
            ) : (
                <ul className="divide-y divide-paper-border-soft">
                    {notes.map((note) => (
                        <li key={note.id} className="px-6 py-4 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm text-ink whitespace-pre-wrap break-words">{note.note_text}</p>
                                <p className="mt-1.5 text-xs text-ink-subtle">
                                    {note.author_name} · {formatDateTime(note.created_at)}
                                </p>
                            </div>
                            {isOwner && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Delete this note? This is audit-logged but cannot be undone.')) {
                                            deleteNote.mutate(note.id);
                                        }
                                    }}
                                    className="text-ink-muted hover:text-status-danger transition-colors flex-shrink-0 mt-0.5"
                                    aria-label="Delete note"
                                    disabled={deleteNote.isPending}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// =============================================================================
// EarlyArrivalRow — personal "be on location early" instruction (inside Notes)
// =============================================================================
// Stored as drivers.early_arrival_minutes (NULL = off). When set, this
// chauffeur's own job emails and status page show "Be on location by HH:MM"
// in orange. Pickup time itself is never changed.
function EarlyArrivalRow({ driver, canEdit }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const current = driver.early_arrival_minutes ? Number(driver.early_arrival_minutes) : null;

    const save = useMutation({
        mutationFn: (mins) => api.patch(`/drivers/${driver.id}`, { early_arrival_minutes: mins }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['driver', driver.id] });
            toast.success('Saved');
        },
        onError: (err) => toast.error(err.message || 'Could not save'),
    });

    return (
        <div className="card-body border-b border-paper-border-soft">
            <label className={`flex items-start gap-2 ${canEdit ? 'cursor-pointer' : ''}`}>
                <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!current}
                    disabled={!canEdit || save.isPending}
                    onChange={(e) => save.mutate(e.target.checked ? 15 : null)}
                />
                <span className="text-sm">
                    <span className="font-medium text-ink">Timekeeping issue</span>
                    <span className="block text-xs text-ink-subtle">
                        Adds "Must be on location by HH:MM" to this chauffeur's own jobs. Pickup time is unchanged.
                    </span>
                </span>
            </label>
            {current && (
                <div className="mt-2 ml-6 flex items-center gap-2">
                    <select
                        value={current}
                        onChange={(e) => save.mutate(Number(e.target.value))}
                        disabled={!canEdit || save.isPending}
                        className="text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink"
                    >
                        {[10, 15, 20, 30, 45].map((m) => (
                            <option key={m} value={m}>{m} min</option>
                        ))}
                    </select>
                    <span className="text-xs text-ink-subtle">before pickup</span>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// Single document row
// =============================================================================
function DocumentRow({ label, requiresExpiry, document: doc, isAdmin, waived, onWaive, onUpload, onPreview, onDelete, isAdditional }) {
    const expiryStatus = doc && doc.expires_at ? computeExpiryStatus(doc.expires_at) : null;
        const clickable = Boolean(doc && onPreview);
    const isApproved = !doc?.approval_status || doc.approval_status === 'approved';

    return (
        <div
            className={
                'px-6 py-4 flex items-start justify-between gap-4 transition-colors ' +
                (clickable ? 'cursor-pointer hover:bg-paper-hover/50' : '')
            }
            onClick={clickable ? onPreview : undefined}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink">{label}</span>
                    {!doc && !isAdditional && (
                        waived
                            ? <StatusPill status="neutral">Not required</StatusPill>
                            : <StatusPill status="danger">Missing</StatusPill>
                    )}
                                        {doc?.approval_status === 'pending' && (
                        <StatusPill status="warning">Awaiting approval</StatusPill>
                    )}
                    {doc?.approval_status === 'rejected' && (
                        <StatusPill status="danger">Rejected</StatusPill>
                    )}
                    {isApproved && expiryStatus && <StatusPill status={expiryStatus.variant}>{expiryStatus.label}</StatusPill>}
                    {isApproved && doc && !doc.expires_at && !requiresExpiry && !isAdditional && (
                        <StatusPill status="success">
                            <CheckCircle2 className="h-3 w-3" />
                            On file
                        </StatusPill>
                    )}
                    {doc && !doc.expires_at && isAdditional && (
                        <StatusPill status="neutral">
                            <FileText className="h-3 w-3" />
                            No expiry
                        </StatusPill>
                    )}
                </div>
                {doc && (
                    <div className="mt-1 text-xs text-ink-subtle">
                        {doc.file_name}
                        {doc.expires_at && ` · Expires ${formatDate(doc.expires_at)}`}
                        · Uploaded {formatDate(doc.uploaded_at)}
                    </div>
                )}
            </div>
           <div className="flex items-center gap-2 flex-shrink-0">
                {!doc && !isAdditional && onWaive && (
                    waived ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onWaive(false); }}
                            className="text-xs text-ink-subtle hover:text-ink underline decoration-dotted"
                            title="Mark this document as required again"
                        >
                            Mark required
                        </button>
                    ) : (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); onWaive(true); }}
                            title="Mark as not required — stops showing under Needs attention"
                        >
                            <Check className="h-3.5 w-3.5" />
                            Not required
                        </Button>
                    )
                )}
                {isAdmin && onUpload && (
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpload();
                        }}
                    >
                        <Upload className="h-3.5 w-3.5" />
                        {doc ? 'Replace' : 'Upload'}
                    </Button>
                )}
                {isAdditional && doc && onDelete && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        className="p-2 rounded-md text-ink-muted hover:text-status-danger hover:bg-red-50 transition-colors"
                        aria-label="Delete document"
                        title="Delete document (owner only)"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// Pairing modal
// =============================================================================
function PairingModal({ driverId, currentPairing, open, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [vehicleId, setVehicleId] = useState('');
    const [notes, setNotes] = useState('');

    const vehiclesQuery = useQuery({
        queryKey: ['vehicles', { status: 'active' }],
        queryFn: () => api.get('/vehicles', { query: { status: 'active' } }),
    });

    const setPairing = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/pairing`, { vehicle_id: vehicleId, notes: notes || undefined }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pairing', 'driver', driverId] });
            toast.success('Vehicle paired');
            onClose();
        },
        onError: (err) => toast.error(err.message || 'Could not pair vehicle'),
    });

    const removePairing = useMutation({
        mutationFn: () => api.delete(`/drivers/${driverId}/pairing`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pairing', 'driver', driverId] });
            toast.success('Pairing removed');
            onClose();
        },
        onError: (err) => toast.error(err.message || 'Could not remove pairing'),
    });

    const vehicles = vehiclesQuery.data?.vehicles || [];

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={currentPairing ? 'Change usual vehicle' : 'Pair usual vehicle'}
            description="This is a soft pairing — the vehicle stays fully available for any chauffeur to be assigned by dispatch."
            size="md"
        >
            <div className="space-y-4">
                <Select
                    label="Vehicle"
                    required
                    value={vehicleId}
                    onChange={(e) => setVehicleId(e.target.value)}
                >
                    <option value="">Select a vehicle…</option>
                    {vehicles.map(v => (
                        <option key={v.id} value={v.id}>
                            {v.nickname || v.registration} — {v.make} {v.model} ({v.registration})
                        </option>
                    ))}
                </Select>
                <Textarea
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Marco's own rental, subcontractor vehicle"
                    rows={2}
                />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 pt-4 border-t border-paper-border-soft">
                <div>
                    {currentPairing && (
                        <Button
                            variant="secondary"
                            onClick={() => removePairing.mutate()}
                            loading={removePairing.isPending}
                            className="text-status-danger"
                        >
                            <Unlink className="h-4 w-4" />
                            Remove pairing
                        </Button>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => setPairing.mutate()}
                        loading={setPairing.isPending}
                        disabled={!vehicleId}
                    >
                        <LinkIcon className="h-4 w-4" />
                        {currentPairing ? 'Change' : 'Pair'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function DisableModal({ driverId, driverName, open, onClose, onDisabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/disable`, { reason }),
        onSuccess: () => {
            toast.success('Chauffeur disabled');
            onDisabled();
        },
        onError: (err) => toast.error(err.message || 'Could not disable chauffeur'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Disable chauffeur?"
            description={`${driverName} will no longer be eligible for new job assignments. Existing assigned jobs remain.`}
            size="md"
        >
            <Textarea
                label="Reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. left the company, long-term leave, document issue"
                rows={3}
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!reason.trim()}
                >
                    <UserX className="h-4 w-4" />
                    Disable
                </Button>
            </div>
        </Modal>
    );
}

function EnableModal({ driverId, driverName, open, onClose, onEnabled }) {
    const api = useApi();
    const [reason, setReason] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/drivers/${driverId}/enable`, reason.trim() ? { reason: reason.trim() } : {}),
        onSuccess: () => {
            toast.success('Chauffeur re-enabled');
            onEnabled();
        },
        onError: (err) => toast.error(err.message || 'Could not re-enable chauffeur'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Re-enable chauffeur?"
            description={`${driverName} will become eligible for new job assignments again.`}
            size="md"
        >
            <Textarea
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. returned from leave, document issue resolved"
                rows={3}
                hint="Optional — helpful context for the audit log."
            />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <UserCheck className="h-4 w-4" />
                    Re-enable
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// PortalAccessCard — Slice 9.1 Chunk E
// =============================================================================
// Three-state portal invite control. Hidden for fleet chauffeurs (their docs
// are managed by the partner — they don't get portal access).
//
//   State 1 — never invited (portal_invited_at IS NULL, portal_first_signed_in_at IS NULL)
//             → "Send portal invite" button
//   State 2 — invited, not signed in (portal_invited_at IS NOT NULL, portal_first_signed_in_at IS NULL)
//             → "Resend invite" button + "Sent X ago, awaiting first sign-in"
//   State 3 — signed in (portal_first_signed_in_at IS NOT NULL)
//             → green "Active in portal" + "First signed in [date]"
//
// Backend stamps portal_invited_at on every send (initial OR resend), so
// "Sent X ago" always reflects the most recent send. portal_first_signed_in_at
// is stamped once by the requirePortalUser middleware on first resolution.
//
// Provisional placement (between PairingCard and Disable/Enable). Will be
// folded into a unified actions section in Slice 9.2.
// =============================================================================
function PortalAccessSubsection({ driver }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const isSignedIn = Boolean(driver.portal_first_signed_in_at);
    const isInvited = Boolean(driver.portal_invited_at);
    const canSend = driver.status === 'active' && Boolean(driver.email);

    // Direct portal-invite mutation. Clerk handles the actual email flow
    // (webhook → Resend), so there's no synchronous body to preview here.
    const inviteMutation = useMutation({
        mutationFn: () => api.post(`/drivers/${driver.id}/portal-invite`, {}),
        onSuccess: () => {
            toast.success(isInvited ? 'Invite resent' : 'Portal invite sent');
            queryClient.invalidateQueries({ queryKey: ['driver', driver.id] });
        },
        onError: (err) => {
            toast.error(err.message || 'Could not send invite');
        },
    });

    return (
        <div className="mt-4 pt-4 border-t border-paper-border-soft">
            <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium mb-2">
                Portal access
            </div>
            {isSignedIn ? (
                <div>
                    <StatusPill status="success">
                        <CheckCircle2 className="h-3 w-3" />
                        Active in portal
                    </StatusPill>
                    <p className="text-xs text-ink-subtle mt-2">
                        First signed in {formatDate(driver.portal_first_signed_in_at)}
                    </p>
                </div>
            ) : isInvited ? (
                <div>
                    <StatusPill status="warning">
                        <Clock className="h-3 w-3" />
                        Awaiting first sign-in
                    </StatusPill>
                    <p className="text-xs text-ink-subtle mt-2">
                        Invite sent {formatDate(driver.portal_invited_at)}.
                    </p>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => inviteMutation.mutate()}
                        disabled={!canSend || inviteMutation.isPending}
                        loading={inviteMutation.isPending}
                        className="mt-3 w-full"
                    >
                        <Mail className="h-3.5 w-3.5" />
                        Resend invite
                    </Button>
                </div>
            ) : (
                <div>
                    <p className="text-sm text-ink-subtle">
                        This chauffeur hasn't been invited to the partner portal yet.
                    </p>
                    {!driver.email && (
                        <p className="text-xs text-status-warning mt-2">
                            Add an email address before sending an invite.
                        </p>
                    )}
                    {driver.status !== 'active' && (
                        <p className="text-xs text-status-warning mt-2">
                            Re-enable this chauffeur before sending an invite.
                        </p>
                    )}
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => inviteMutation.mutate()}
                        disabled={!canSend || inviteMutation.isPending}
                        loading={inviteMutation.isPending}
                        className="mt-3 w-full"
                    >
                        <Mail className="h-3.5 w-3.5" />
                        Send portal invite
                    </Button>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// Delete document modal — hard delete, "other" only, admin/owner only (round 7b.6)
// =============================================================================
function DeleteDocumentModal({ driverId, document: doc, open, onClose, onDeleted }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: () => api.delete(`/documents/${doc.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents', 'driver', driverId] });
            toast.success('Document deleted');
            onDeleted();
        },
        onError: (err) => toast.error(err.message || 'Could not delete document'),
    });

    const label = doc.custom_label || formatDocType(doc.doc_type);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete document?"
            description="This permanently removes the file from storage and the record from the database. This action cannot be undone."
            size="md"
        >
            <div className="space-y-3 text-sm">
                <div className="p-3 rounded-md border border-paper-border bg-paper-hover/40">
                    <div className="font-medium text-ink">{label}</div>
                    <div className="text-xs text-ink-subtle mt-1">{doc.file_name}</div>
                    {doc.expires_at && (
                        <div className="text-xs text-ink-subtle mt-0.5">Expires {formatDate(doc.expires_at)}</div>
                    )}
                    <div className="text-xs text-ink-subtle mt-0.5">Uploaded {formatDate(doc.uploaded_at)}</div>
                </div>
                <p className="text-xs text-ink-subtle">
                    The deletion is audit-logged (who, when, which file).
                </p>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                >
                    <Trash2 className="h-4 w-4" />
                    Delete permanently
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Profile picture avatar
// =============================================================================
function ChauffeurAvatar({ driverId, profilePictureDoc }) {
    const api = useApi();

    const { data } = useQuery({
        queryKey: ['document-download-url', profilePictureDoc?.id],
        queryFn: () => api.get(`/documents/${profilePictureDoc.id}/download-url`),
        enabled: Boolean(profilePictureDoc),
        staleTime: 30_000,
    });

    if (!profilePictureDoc || !data?.download_url) {
        // Blank placeholder — stays out of the way until a picture is uploaded
        return null;
    }

    return (
        <img
            src={data.download_url}
            alt="Profile"
            className="h-12 w-12 rounded-full object-cover border border-paper-border"
        />
    );
}

// =============================================================================
// EditableExpiry — inline editable date within the doc preview metadata strip
// =============================================================================
function EditableExpiry({ doc, ownerType, ownerId, requiresExpiry, isOwner }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(
        doc.expires_at ? new Date(doc.expires_at).toISOString().slice(0, 10) : ''
    );
    const [savedOverride, setSavedOverride] = useState(null);

    const mutation = useMutation({
        mutationFn: () => api.patch(`/documents/${doc.id}`, {
            expires_at: value && value.trim() !== '' ? value : null,
        }),
        onSuccess: () => {
    setSavedOverride(value && value.trim() !== '' ? value : '');
// The documents list lives in its own query, keyed as ['documents', ownerType, ownerId].
// Invalidate both that one AND the parent entity (in case anything reads from it).
queryClient.refetchQueries({ queryKey: ['documents', ownerType, ownerId] });
queryClient.invalidateQueries({ queryKey: [ownerType, ownerId] });
queryClient.invalidateQueries({ queryKey: ['document-download-url', doc.id] });
    toast.success('Expiry updated');
    setEditing(false);
},
        onError: (err) => {
            toast.error(err?.message || 'Could not update expiry');
        },
    });

// Use the just-saved value if we have one, otherwise the prop value.
// This bridges the gap between save-success and the parent refetch.
const displayedExpiry = savedOverride !== null ? savedOverride : doc.expires_at;

if (!editing) {
    if (!displayedExpiry) {
            // No expiry set — show "Set expiry" link only for owner
            if (!isOwner) return null;
            return (
                <button
                    type="button"
                    onClick={() => { setValue(''); setEditing(true); }}
                    className="text-ink-subtle hover:text-ink underline decoration-dotted"
                >
                    Set expiry
                </button>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5">
                <span>Expires {formatDate(displayedExpiry)}</span>
                {isOwner && (
                    <button
                        type="button"
                        onClick={() => {
                           setValue(new Date(displayedExpiry).toISOString().slice(0, 10));
                            setEditing(true);
                        }}
                        className="text-ink-subtle hover:text-ink p-0.5 rounded"
                        title="Edit expiry"
                        aria-label="Edit expiry"
                    >
                        <Pencil className="h-3 w-3" />
                    </button>
                )}
            </span>
        );
    }

    // Edit mode — small inline form: date input + Save + Cancel
    const canSave = !mutation.isPending && (value !== '' || !requiresExpiry);

    return (
        <span className="inline-flex items-center gap-1.5">
            <input
                type="date"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="px-1.5 py-0.5 text-xs border border-paper-border rounded focus:outline-none focus:ring-1 focus:ring-ink/30"
                disabled={mutation.isPending}
                autoFocus
            />
            <button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={!canSave}
                className="p-0.5 rounded bg-ink text-white disabled:opacity-50"
                title="Save"
                aria-label="Save"
            >
                <Check className="h-3 w-3" />
            </button>
            <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={mutation.isPending}
                className="p-0.5 rounded text-ink-subtle hover:text-ink"
                title="Cancel"
                aria-label="Cancel"
            >
                <X className="h-3 w-3" />
            </button>
        </span>
    );
}

// =============================================================================
// Document preview modal
// =============================================================================
function DocumentPreviewModal({ document: doc, open, onClose, ownerId, isOwner }) {
    const api = useApi();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['document-download-url', doc.id],
        queryFn: () => api.get(`/documents/${doc.id}/download-url`),
        enabled: open,
        staleTime: 30_000, // signed URL valid 60s, cache half that
    });

    const downloadUrl = data?.download_url;
    const mimeType = doc.mime_type || '';
    const isImage = mimeType.startsWith('image/');
    const isPdf = mimeType === 'application/pdf';

    function handleDownload() {
        if (!downloadUrl) return;
        const a = window.document.createElement('a');
        a.href = downloadUrl;
        a.download = doc.file_name;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={doc.custom_label || formatDocType(doc.doc_type)}
            description={doc.file_name}
            size="lg"
        >
            <div className="space-y-4">
                {/* Metadata strip */}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-subtle">
                    <span>Uploaded {formatDateTime(doc.uploaded_at)}</span>
                    <EditableExpiry
    doc={doc}
    ownerType="driver"
    ownerId={ownerId}
    requiresExpiry={['driving_licence','phv_licence','phd_licence','phd_badge','mot','insurance'].includes(doc.doc_type)}
    isOwner={isOwner}
/>
                    {doc.file_size && <span>{formatBytes(doc.file_size)}</span>}
                    <span className="font-mono">{mimeType || 'unknown type'}</span>
                </div>

                {/* Preview area */}
                <div className="border border-paper-border rounded-md overflow-hidden bg-paper-hover/30" style={{ minHeight: '400px' }}>
                    {isLoading && (
                        <div className="flex items-center justify-center h-96 text-ink-subtle text-sm">
                            Loading preview…
                        </div>
                    )}
                    {isError && (
                        <div className="flex items-center justify-center h-96 text-status-danger text-sm">
                            Could not load preview: {error?.message || 'Unknown error'}
                        </div>
                    )}
                    {downloadUrl && isImage && (
                        <img
                            src={downloadUrl}
                            alt={doc.file_name}
                            className="w-full h-auto max-h-[70vh] object-contain bg-black/5"
                        />
                    )}
                    {downloadUrl && isPdf && (
                        <iframe
                            src={downloadUrl}
                            title={doc.file_name}
                            className="w-full h-[70vh] border-0"
                        />
                    )}
                    {downloadUrl && !isImage && !isPdf && (
                        <div className="flex flex-col items-center justify-center h-96 text-center p-6">
                            <FileText className="h-12 w-12 text-ink-subtle mb-3" />
                            <p className="text-sm text-ink">Preview not available for this file type.</p>
                            <p className="text-xs text-ink-subtle mt-1">Use the Download button below to view it.</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button onClick={handleDownload} disabled={!downloadUrl}>
                    <Download className="h-4 w-4" />
                    Download
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Helpers
// =============================================================================
async function downloadCompliancePdf(getToken, driverId, driver) {
    try {
        const token = await getToken();
        const res = await fetch(`/api/drivers/${driverId}/compliance-pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Failed (${res.status})`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeName = `${driver.first_name}-${driver.last_name}`.replace(/[^a-zA-Z0-9-]/g, '_');
        a.download = `Lanz-CTS-Chauffeur-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Download profile PDF downloaded');
    } catch (err) {
        toast.error(err.message || 'Could not generate PDF');
    }
}

function computeExpiryStatus(expiresAt) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiresAt);
    const daysOut = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysOut < 0) return { variant: 'danger', label: 'Expired' };
    if (daysOut <= 7) return { variant: 'danger', label: `Expires in ${daysOut}d` };
    if (daysOut <= 14) return { variant: 'warning', label: `Expires in ${daysOut}d` };
    if (daysOut <= 30) return { variant: 'warning', label: `Expires ${formatDate(expiresAt)}` };
    return { variant: 'success', label: `Valid until ${formatDate(expiresAt)}` };
}

function formatDate(d) {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return null;
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatDocType(type) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
